import { API_REQUEST_TIMEOUT_MS, isFetchAbortError } from "@evolua/api-client";
import { getApiBaseUrl } from "./config.js";

export type TokenBundle = {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
};

export type AuthChallenge = {
  challengeName: string;
  session: string;
  challengeParameters?: Record<string, string>;
};

export type LoginResult =
  | { kind: "tokens"; tokens: TokenBundle }
  | { kind: "challenge"; challenge: AuthChallenge };

export type ProblemBody = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
};

export class AuthApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly detail?: string;

  constructor(status: number, body: ProblemBody | null) {
    super(body?.detail || body?.title || `Erro HTTP ${status}`);
    this.name = "AuthApiError";
    this.status = status;
    if (body?.code !== undefined) this.code = body.code;
    if (body?.detail !== undefined) this.detail = body.detail;
  }
}

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function mapAuthFetchFailure(err: unknown): AuthApiError {
  if (isFetchAbortError(err)) {
    return new AuthApiError(0, {
      code: "TIMEOUT",
      detail:
        "A API demorou para responder. Em ambiente free ela pode estar iniciando — aguarde ~30–60s e tente de novo.",
      title: "Timeout",
    });
  }
  return new AuthApiError(0, {
    code: "NETWORK_ERROR",
    detail:
      "Falha de rede ao falar com a API. Verifique a conexão ou se o serviço está no ar.",
    title: "Network Error",
  });
}

async function request(
  path: string,
  init: RequestInit & { accessToken?: string } = {},
): Promise<Response> {
  const { accessToken, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (rest.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  const { signal: userSignal, ...restInit } = rest;
  const timeoutSignal = AbortSignal.timeout(API_REQUEST_TIMEOUT_MS);
  const anyFn = (
    AbortSignal as typeof AbortSignal & {
      any?: (signals: AbortSignal[]) => AbortSignal;
    }
  ).any;
  const signal =
    userSignal && typeof anyFn === "function"
      ? anyFn([userSignal, timeoutSignal])
      : timeoutSignal;

  try {
    return await fetch(`${getApiBaseUrl()}${path}`, {
      ...restInit,
      headers,
      signal,
    });
  } catch (err) {
    throw mapAuthFetchFailure(err);
  }
}

async function expectOk(res: Response): Promise<unknown> {
  if (res.ok) {
    if (res.status === 204) return null;
    return parseJson(res);
  }
  const body = (await parseJson(res)) as ProblemBody | null;
  throw new AuthApiError(res.status, body);
}

function isTokenBundle(v: unknown): v is TokenBundle {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.accessToken === "string" &&
    typeof o.idToken === "string" &&
    typeof o.refreshToken === "string" &&
    typeof o.expiresIn === "number"
  );
}

function isChallenge(v: unknown): v is AuthChallenge {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return typeof o.challengeName === "string" && typeof o.session === "string";
}

export async function loginRequest(
  email: string,
  password: string,
): Promise<LoginResult> {
  const res = await request("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  const data = await expectOk(res);
  if (isChallenge(data)) {
    const challenge: AuthChallenge = {
      challengeName: data.challengeName,
      session: data.session,
    };
    if (data.challengeParameters) {
      challenge.challengeParameters = data.challengeParameters;
    }
    return { kind: "challenge", challenge };
  }
  if (isTokenBundle(data)) {
    return { kind: "tokens", tokens: data };
  }
  throw new AuthApiError(500, {
    detail: "Resposta de login inesperada.",
    code: "login-shape",
  });
}

export async function refreshRequest(refreshToken: string): Promise<TokenBundle> {
  const res = await request("/v1/auth/refresh", {
    method: "POST",
    body: JSON.stringify({ refreshToken }),
  });
  const data = await expectOk(res);
  if (!isTokenBundle(data)) {
    throw new AuthApiError(500, {
      detail: "Resposta de refresh inesperada.",
      code: "refresh-shape",
    });
  }
  return data;
}

export async function logoutRequest(opts: {
  refreshToken?: string | null;
  accessToken?: string | null;
  global?: boolean;
}): Promise<void> {
  const init: RequestInit & { accessToken?: string } = {
    method: "POST",
    body: JSON.stringify({
      ...(opts.refreshToken ? { refreshToken: opts.refreshToken } : {}),
      global: opts.global === true,
    }),
  };
  if (opts.accessToken) init.accessToken = opts.accessToken;
  await expectOk(await request("/v1/auth/logout", init));
}

const associateBySession = new Map<
  string,
  Promise<{ secretCode: string; session: string; otpauthIssuer: string }>
>();

export async function mfaAssociate(session: string): Promise<{
  secretCode: string;
  session: string;
  otpauthIssuer: string;
}> {
  const cached = associateBySession.get(session);
  if (cached) return cached;

  const pending = (async () => {
    const data = await expectOk(
      await request("/v1/auth/mfa/totp/associate", {
        method: "POST",
        body: JSON.stringify({ session }),
      }),
    );
    const o = data as Record<string, unknown>;
    if (typeof o.secretCode !== "string" || typeof o.session !== "string") {
      throw new AuthApiError(500, { detail: "Resposta MFA associate inválida." });
    }
    return {
      secretCode: o.secretCode,
      session: o.session,
      otpauthIssuer: typeof o.otpauthIssuer === "string" ? o.otpauthIssuer : "Evolua HCM",
    };
  })().catch((err: unknown) => {
    associateBySession.delete(session);
    throw err;
  });

  associateBySession.set(session, pending);
  return pending;
}

export async function mfaVerifySetup(body: {
  session: string;
  code: string;
  email: string;
  friendlyDeviceName?: string;
}): Promise<TokenBundle & { recoveryCodes: string[] }> {
  const data = await expectOk(
    await request("/v1/auth/mfa/totp/verify-setup", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );
  if (!isTokenBundle(data)) {
    throw new AuthApiError(500, { detail: "Resposta MFA verify-setup inválida." });
  }
  const codes = (data as { recoveryCodes?: unknown }).recoveryCodes;
  return {
    ...data,
    recoveryCodes: Array.isArray(codes) ? codes.map(String) : [],
  };
}

export async function mfaChallenge(body: {
  session: string;
  code: string;
  email: string;
}): Promise<TokenBundle> {
  const data = await expectOk(
    await request("/v1/auth/mfa/challenge", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );
  if (!isTokenBundle(data)) {
    throw new AuthApiError(500, { detail: "Resposta MFA challenge inválida." });
  }
  return data;
}

export async function mfaRecovery(body: {
  email: string;
  password: string;
  recoveryCode: string;
}): Promise<LoginResult> {
  const data = await expectOk(
    await request("/v1/auth/mfa/recovery", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );
  if (!data || typeof data !== "object") {
    throw new AuthApiError(500, { detail: "Resposta MFA recovery inválida." });
  }
  const o = data as Record<string, unknown>;
  if (isChallenge(o)) {
    return { kind: "challenge", challenge: o };
  }
  if (o.tokens && isTokenBundle(o.tokens)) {
    return { kind: "tokens", tokens: o.tokens };
  }
  if (isTokenBundle(o)) {
    return { kind: "tokens", tokens: o };
  }
  throw new AuthApiError(500, { detail: "Resposta MFA recovery inválida." });
}

export async function passwordForgot(email: string): Promise<{
  message: string;
  devToken?: string;
}> {
  const data = await expectOk(
    await request("/v1/auth/password/forgot", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  );
  const o = (data ?? {}) as Record<string, unknown>;
  const result: { message: string; devToken?: string } = {
    message:
      typeof o.message === "string"
        ? o.message
        : "Se a conta existir, enviamos instruções de recuperação.",
  };
  if (typeof o.devToken === "string") result.devToken = o.devToken;
  return result;
}

export async function passwordConfirm(body: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<{ message: string }> {
  const data = await expectOk(
    await request("/v1/auth/password/confirm", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );
  const o = (data ?? {}) as Record<string, unknown>;
  return {
    message: typeof o.message === "string" ? o.message : "Senha atualizada.",
  };
}
